import { Badge } from "@/components/ui/badge.tsx";
import { Input } from "@/components/ui/input.tsx";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { AddUser } from "@/components/users/add-user.tsx";
import { useGroups } from "@/lib/api/group";
import { useDebounce } from "@/lib/hooks/use-debounce.ts";
import { cn } from "@/lib/utils.ts";
import { useRouter, useSearch } from "@tanstack/react-router";
import { SearchIcon } from "lucide-react";
import { useEffect, useState } from "react";

const FILTERS = [
  {
    label: "All",
    value: "all",
  },
  {
    label: "Admins",
    value: "ADMIN",
  },
  {
    label: "Super Admins",
    value: "SUPERADMIN",
  },
  {
    label: "Teens",
    value: "MENTEE",
  },
  {
    label: "Mentors",
    value: "MENTOR",
  },
];

const STATUS = [
  {
    label: "Active",
    value: "active",
  },
  {
    label: "Inactive",
    value: "inactive",
  },
];

const PROFILE_COMPLETENESS = [
  {
    label: "All Profiles",
    value: "all",
  },
  {
    label: "Complete Profiles",
    value: "complete",
  },
  {
    label: "Incomplete Profiles",
    value: "incomplete",
  },
];

export function Filters() {
  const {
    role,
    per_page,
    page,
    search: urlSearch,
    group: urlGroup,
    status: statusGroup,
    profileCompleteness: urlProfileCompleteness,
  } = useSearch({
    strict: false,
  }) as Record<string, string>;

  const router = useRouter();
  const [search, setSearch] = useState("");
  const [customFilter, setCustomFilter] =
    useState<(typeof FILTERS)[number]["value"]>("all");
  const { data: groups } = useGroups();

  const debouncedSearch = useDebounce(search, 500);

  useEffect(() => {
    router.navigate({
      search: {
        search: debouncedSearch,
        page: page,
        role,
        per_page,
        customFilter,
        group: urlGroup,
        status: statusGroup,
        profileCompleteness: urlProfileCompleteness,
      } as any,
    });
  }, [debouncedSearch]);

  useEffect(() => {
    if (urlSearch) {
      setSearch(urlSearch);
    }
  }, [urlSearch]);

  return (
    <div className="mb-2 flex flex-col gap-4 p-2">
      <div className="flex items-center gap-4">
        <div className="flex flex-1 items-center gap-2">
          {FILTERS.map((filter) => (
            <Badge
              key={filter.value}
              variant={customFilter === filter.value ? "secondary" : "outline"}
              className={cn("cursor-pointer px-4 py-2 capitalize", {
                "bg-black text-white hover:bg-black":
                  customFilter === filter.value,
              })}
              onClick={() => {
                setCustomFilter(filter.value);
                router.navigate({
                  search: {
                    search: debouncedSearch,
                    page: 1,
                    role,
                    per_page,
                    customFilter: filter.value,
                    group: urlGroup,
                    status: statusGroup,
                    profileCompleteness: urlProfileCompleteness,
                  } as any,
                });
              }}
            >
              {filter.label}
            </Badge>
          ))}
        </div>

        <div>
          <Select
            value={statusGroup || "any"}
            onValueChange={(value) => {
              router.navigate({
                search: ((old: any) => ({
                  ...old,
                  status: value,
                  page: 1,
                })) as any,
              });
            }}
          >
            <SelectTrigger className="w-[200px]">
              <SelectValue placeholder="Any Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="any">Any Status</SelectItem>
              {STATUS?.map((status) => (
                <SelectItem key={status.label} value={status.value}>
                  {status.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Select
            value={urlGroup || "all"}
            onValueChange={(value) => {
              router.navigate({
                search: ((old: any) => ({
                  ...old,
                  group: value,
                  page: "1",
                })) as any,
              });
            }}
          >
            <SelectTrigger className="w-[200px]">
              <SelectValue placeholder="Select Group" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Groups</SelectItem>
              {groups?.map((group) => (
                <SelectItem key={group.id} value={group.id}>
                  {group.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Select
            value={urlProfileCompleteness || "all"}
            onValueChange={(value) => {
              router.navigate({
                search: ((old: any) => ({
                  ...old,
                  profileCompleteness: value === "all" ? undefined : value,
                  page: "1",
                })) as any,
              });
            }}
          >
            <SelectTrigger className="w-[200px]">
              <SelectValue placeholder="Profile Status" />
            </SelectTrigger>
            <SelectContent>
              {PROFILE_COMPLETENESS.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-center gap-4">
          <Input
            placeholder="Search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            startIcon={<SearchIcon className="h-5 w-5" />}
            className="w-72s w-[300px] rounded-md"
          />
          <AddUser />
        </div>
      </div>
    </div>
  );
}
